export interface ParsedFunctionCall {
    name: string;
    args: Record<string, string>;
}

/**
 * Opening tags, matched leniently on purpose — see `parseFunctionCallsFromText` for the two very
 * different leaks these have to cover.
 *
 * Every part that can go missing is optional: the leading `<`, the `antml:` namespace prefix, and
 * the closing `>` of an `invoke`. What is NOT optional is `name="…"`, which is what keeps this from
 * matching prose.
 */
const INVOKE_OPEN_REGEX = /<?\s*(?:antml:)?invoke\s+name="([^"]+)"\s*>?/g;
const PARAMETER_REGEX = /<?\s*(?:antml:)?parameter\s+name="([^"]+)"\s*>([\s\S]*?)<\/(?:antml:)?parameter\s*>/g;

/** Where the prose stops and the leaked markup starts — used to trim what the user is shown. */
const LEAK_START_REGEX = /<?\s*(?:antml:)?(?:function_calls\s*>|invoke\s+name=")/;

/** A closed `invoke`, used to tell a finished call from a reply that was cut off inside one. */
const INVOKE_CLOSE_REGEX = /<\/(?:antml:)?invoke\s*>/;

/**
 * Stripping the leak back out of stored text, in two passes because the two forms nest differently:
 * the wrapped form must go whole (stopping at the first `</invoke>` would leave a dangling
 * `</function_calls>`), and the bare form has no wrapper to stop at.
 */
const WRAPPED_LEAK_REGEX = /<(?:antml:)?function_calls\s*>[\s\S]*?<\/(?:antml:)?function_calls\s*>/g;
const BARE_INVOKE_REGEX = /<?\s*(?:antml:)?invoke\s+name="[^"]*"[\s\S]*?(?:<\/(?:antml:)?invoke\s*>|$)/g;

/**
 * Recovers tool calls that a model wrote into its reply text instead of returning as structured
 * `tool_calls`.
 *
 * **Two leaks, not one, and they look nothing alike.**
 *
 * `claude-haiku-4-5` emits well-formed pseudo-XML:
 * `<function_calls><invoke name="create_block"><parameter name="index">0</parameter></invoke></function_calls>`
 *
 * `claude-opus-5` (observed 2026-08-22, the first run after the switch) emits the `antml:`-namespaced
 * form with no wrapper and with opening angle brackets missing:
 * ```
 * antml:invoke name="apply_operations"
 * antml:parameter name="operations">[ … ]</parameter>
 * ```
 * Against the original strict regexes that matched nothing at all: no calls ran, no error appeared,
 * and the raw markup was rendered into the chat bubble for the user to read. "Nothing happens" is
 * the worst possible failure here, which is why this now matches leniently rather than exactly.
 *
 * Parsed by scanning for `invoke` openings and taking each body as everything up to the next
 * opening, a closing `</invoke>`, or the end of the text — rather than by requiring a closing tag,
 * which a truncated reply will not have either.
 */
export function parseFunctionCallsFromText(text: string, knownTools: readonly string[] = []): ParsedFunctionCall[] {
    const calls: ParsedFunctionCall[] = [];
    const opens = [...text.matchAll(INVOKE_OPEN_REGEX)];

    for (const [position, open] of opens.entries()) {
        const bodyStart = open.index + open[0].length;
        const nextOpen = opens[position + 1]?.index ?? text.length;
        const body = text.slice(bodyStart, nextOpen);
        const args: Record<string, string> = {};

        for (const paramMatch of body.matchAll(PARAMETER_REGEX)) {
            const [, paramName, paramValue] = paramMatch;
            args[paramName] = paramValue.trim();
        }

        // A reply cut off mid-tag reaches here having named a tool and nothing else. Running it
        // would execute a create with no arguments — a side effect the user never asked for, from a
        // sentence the model never finished. So a call counts only once something completed: at
        // least one parameter parsed, or an explicit `</invoke>` (which is how a genuinely
        // argument-less call, like `get_page_snapshot`, still gets through).
        if (Object.keys(args).length === 0 && !INVOKE_CLOSE_REGEX.test(body)) {
            continue;
        }

        calls.push({ name: open[1], args });
    }

    // The recognizer runs ONLY when the markup parser came up empty, so a well-formed reply is never
    // reinterpreted — see `recognizeBareToolCalls`.
    return dedupeAdjacentCalls(calls.length || !knownTools.length ? calls : recognizeBareToolCalls(text, knownTools));
}

/**
 * The JSON value that follows a tool name — read by counting brackets rather than by regex, because
 * the payload nests arbitrarily deep and contains braces inside its own strings.
 */
function readJsonValueAt(text: string, start: number): { value: unknown; end: number } | null {
    const open = text[start];
    const close = open === '{' ? '}' : ']';
    let depth = 0;
    let inString = false;
    let escaped = false;

    for (let index = start; index < text.length; index++) {
        const character = text[index];

        if (escaped) {
            escaped = false;
            continue;
        }

        if (character === '\\' && inString) {
            escaped = true;
            continue;
        }

        if (character === '"') {
            inString = !inString;
            continue;
        }

        if (inString) {
            continue;
        }

        if (character === open) {
            depth++;
        } else if (character === close) {
            depth--;

            if (depth === 0) {
                try {
                    return { value: JSON.parse(text.slice(start, index + 1)), end: index + 1 };
                } catch {
                    return null;
                }
            }
        }
    }

    return null;
}

/**
 * Last resort: recognize "call THIS tool with THIS JSON" written in no particular syntax.
 *
 * Three different shapes came out of `claude-opus-5` in three consecutive runs of the same prompt,
 * none of them the `<function_calls>` XML the history teaches it:
 *
 * ```
 * antml:invoke name="apply_operations"          ← handled by the markup parser above
 * apply_operations\n\n* { "operations": … }
 * anthropic_tool_call: apply_operations\n{ "operations": … }
 * ```
 *
 * Chasing the syntax was losing, so this matches the INTENT instead: a name from the caller's own
 * tool list, followed by a parseable JSON object. Bounded three ways, because executing something
 * the user did not ask for is far worse than missing a call:
 *
 * - only names the request actually offered are considered — never an arbitrary identifier;
 * - only a JSON OBJECT counts (every tool here takes one), and it must parse;
 * - it runs only when the markup parser found nothing, so a well-formed reply is never reinterpreted.
 *
 * The remaining false positive is a model that DISCUSSES a tool and shows example arguments. The
 * prompt forbids narrating calls, and the alternative — silently doing nothing while the model tells
 * the user it succeeded — is what this exists to stop.
 */
function recognizeBareToolCalls(text: string, knownTools: readonly string[]): ParsedFunctionCall[] {
    const calls: { call: ParsedFunctionCall; at: number }[] = [];

    for (const name of knownTools) {
        const nameRegex = new RegExp(`(?<![\\w-])${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?![\\w-])`, 'g');

        for (const match of text.matchAll(nameRegex)) {
            const jsonStart = text.indexOf('{', match.index + name.length);

            if (jsonStart === -1) {
                continue;
            }

            // The payload has to FOLLOW the name closely; anything further away is prose that
            // happens to mention a tool and, separately, contains an object.
            if (text.slice(match.index + name.length, jsonStart).trim().length > 40) {
                continue;
            }

            const parsed = readJsonValueAt(text, jsonStart);

            if (!parsed || typeof parsed.value !== 'object' || parsed.value === null || Array.isArray(parsed.value)) {
                continue;
            }

            calls.push({
                call: {
                    name,
                    // Values are kept as they parsed rather than stringified per key: this path has
                    // real JSON, so it does not suffer the double-encoding the parameter form does.
                    args: parsed.value as Record<string, string>,
                },
                at: match.index,
            });
        }
    }

    return dedupeAdjacentCalls(calls.sort((a, b) => a.at - b.at).map((entry) => entry.call));
}

/** True when the text carries leaked markup in either form — the caller's signal to convert it. */
export function findLeakStart(text: string): number {
    return text.search(LEAK_START_REGEX);
}

function callSignature(call: ParsedFunctionCall): string {
    const sortedArgs = Object.fromEntries(Object.entries(call.args).sort(([a], [b]) => a.localeCompare(b)));
    return `${call.name}:${JSON.stringify(sortedArgs)}`;
}

/**
 * Some models (observed with claude-haiku-4-5 emulating tool-calling via this text convention)
 * repeat the exact same invocation back-to-back in one response — e.g. two identical, argument-
 * less `create_block` calls for a single "create a block" request. A well-formed response has no
 * legitimate reason to call the same tool with the same arguments twice in a row, so collapse
 * adjacent duplicates rather than executing (and creating side effects for) each one.
 */
function dedupeAdjacentCalls(calls: ParsedFunctionCall[]): ParsedFunctionCall[] {
    return calls.filter((call, index) => index === 0 || callSignature(call) !== callSignature(calls[index - 1]));
}

export function stripFunctionCallsFromText(text: string): string {
    return text.replace(WRAPPED_LEAK_REGEX, '').replace(BARE_INVOKE_REGEX, '').trim();
}
